import { useState } from 'react';
import { Trash2, AlertTriangle, Layers } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useDemoteFunnelToStaging } from '@/lib/api/funnels';
import { Storefront } from '../types';

interface DemoteToStagingDialogProps {
  funnel: Storefront;
  storeId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function DemoteToStagingDialog({
  funnel,
  storeId,
  open,
  onOpenChange,
  onSuccess,
}: DemoteToStagingDialogProps) {
  const [selectedDomain, setSelectedDomain] = useState<string>('');
  const demoteMutation = useDemoteFunnelToStaging();

  const customDomains = funnel.productionDomains || [];

  const handleDemote = async () => {
    if (!selectedDomain) {
      toast.error('Please select a domain to demote');
      return;
    }

    try {
      await demoteMutation.mutateAsync({
        funnelId: funnel.id,
        storeId,
        customDomain: selectedDomain,
      });

      toast.success(`Funnel removed from ${selectedDomain}`);
      if (onSuccess) onSuccess();
      onOpenChange(false);
      setSelectedDomain('');
    } catch (error) {
      console.error('Failed to demote:', error);
      toast.error('Failed to demote funnel');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <Trash2 className="h-5 w-5" />
            Demote from Production
          </DialogTitle>
          <DialogDescription>
            Remove this funnel from a live production domain. It will revert to Staging only.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Warning</AlertTitle>
            <AlertDescription className="text-xs">
              This will immediately <strong>delete all routes</strong> on the selected domain.
              <br />
              Users visiting the production URL will encounter a 404 error.
              <br />
              The <strong>Staging</strong> version will remain unaffected.
            </AlertDescription>
          </Alert>

          <div className="grid gap-2">
            <Label htmlFor="domain-select">Select Production Domain</Label>
            <Select value={selectedDomain} onValueChange={setSelectedDomain}>
              <SelectTrigger id="domain-select">
                <SelectValue placeholder="Select domain to remove..." />
              </SelectTrigger>
              <SelectContent>
                {customDomains.length > 0 ? (
                  customDomains.map((domain) => (
                    <SelectItem key={domain} value={domain}>
                      <div className="flex items-center gap-2">
                        <Layers className="h-3 w-3 text-slate-500" />
                        {domain}
                      </div>
                    </SelectItem>
                  ))
                ) : (
                  <div className="text-muted-foreground p-2 text-center text-sm">
                    No active production domains found.
                  </div>
                )}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDemote}
            disabled={demoteMutation.isPending || !selectedDomain}
          >
            {demoteMutation.isPending ? 'Removing...' : 'Remove from Production'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
